export const DISCUSSION_COMPOSER_TARGETS = Object.freeze({
  ROOT: "root",
  REPLY: "reply",
});

export const DISCUSSION_NAVIGATION_ACTIONS = Object.freeze({
  RESET: "reset",
  OPEN_CHILDREN: "open-children",
  OPEN_REPLY_COMPOSER: "open-reply-composer",
  OPEN_CURRENT_COMPOSER: "open-current-composer",
  OPEN_ROOT_COMPOSER: "open-root-composer",
  CLOSE_COMPOSER: "close-composer",
  CONSUME_SCROLL_TARGET: "consume-scroll-target",
  BACK: "back",
  SELECT_LEVEL: "select-level",
});

function isReply(value) {
  return Boolean(value?.id);
}

function appendReply(path, reply) {
  if (!isReply(reply)) return path;
  return path.at(-1)?.id === reply.id ? path : [...path, reply];
}

function firstChildScrollTarget(parentId = null) {
  return {
    type: "first-child",
    parentId: parentId || null,
  };
}

/**
 * Drawer navigation is a single state machine. Every navigation transition
 * dismisses a composer so it cannot survive into a different reply branch.
 */
export function createDiscussionNavigationState(request = {}) {
  const focusReply = request?.focusReply;
  const composer = request?.composer;
  const focusPath = Array.isArray(request?.focusPath)
    ? request.focusPath.filter(isReply)
    : [];
  const path = focusPath.length ? focusPath : isReply(focusReply) ? [focusReply] : [];

  return {
    path,
    scrollTarget: request?.focusFirstChild ? firstChildScrollTarget() : null,
    composer:
      composer === DISCUSSION_COMPOSER_TARGETS.ROOT
        ? DISCUSSION_COMPOSER_TARGETS.ROOT
        : composer === DISCUSSION_COMPOSER_TARGETS.REPLY && path.length
          ? DISCUSSION_COMPOSER_TARGETS.REPLY
          : null,
  };
}

export function reduceDiscussionNavigation(state, action = {}) {
  const current = state || createDiscussionNavigationState();

  switch (action.type) {
    case DISCUSSION_NAVIGATION_ACTIONS.RESET:
      return createDiscussionNavigationState(action.request);
    case DISCUSSION_NAVIGATION_ACTIONS.OPEN_CHILDREN:
      return {
        path: appendReply(current.path, action.reply),
        composer: null,
        scrollTarget: firstChildScrollTarget(action.reply?.id),
      };
    case DISCUSSION_NAVIGATION_ACTIONS.OPEN_REPLY_COMPOSER:
      return {
        path: appendReply(current.path, action.reply),
        composer: DISCUSSION_COMPOSER_TARGETS.REPLY,
        scrollTarget: null,
      };
    case DISCUSSION_NAVIGATION_ACTIONS.OPEN_CURRENT_COMPOSER:
      return {
        ...current,
        composer: current.path.length
          ? DISCUSSION_COMPOSER_TARGETS.REPLY
          : DISCUSSION_COMPOSER_TARGETS.ROOT,
        scrollTarget: null,
      };
    case DISCUSSION_NAVIGATION_ACTIONS.OPEN_ROOT_COMPOSER:
      return { path: [], composer: DISCUSSION_COMPOSER_TARGETS.ROOT, scrollTarget: null };
    case DISCUSSION_NAVIGATION_ACTIONS.CLOSE_COMPOSER:
      return { ...current, composer: null };
    case DISCUSSION_NAVIGATION_ACTIONS.CONSUME_SCROLL_TARGET:
      return { ...current, scrollTarget: null };
    case DISCUSSION_NAVIGATION_ACTIONS.BACK:
      return { path: current.path.slice(0, -1), composer: null, scrollTarget: null };
    case DISCUSSION_NAVIGATION_ACTIONS.SELECT_LEVEL: {
      const level = Number(action.level);
      return {
        path: Number.isFinite(level) && level > 0 ? current.path.slice(0, level) : [],
        composer: null,
        scrollTarget: null,
      };
    }
    default:
      return current;
  }
}

function selectBranchPath(path, reply, threadId) {
  if (!isReply(reply)) return path;
  if (reply.parentId && reply.parentId !== threadId) {
    const parentIndex = path.indexOf(reply.parentId);
    if (parentIndex >= 0) return [...path.slice(0, parentIndex + 1), reply.id];
  }
  return [reply.id];
}

export function createDiscussionBranchNavigationState(request = {}) {
  const focusReply = request?.focusReply;
  const focusPath = Array.isArray(request?.focusPath)
    ? request.focusPath.filter(isReply)
    : [];
  return {
    activePath: focusPath.length
      ? focusPath.map((reply) => reply.id)
      : isReply(focusReply)
        ? [focusReply.id]
        : [],
    composer: request?.composer || null,
    scrollTarget: request?.focusFirstChild ? firstChildScrollTarget() : null,
  };
}

export function reduceDiscussionBranchNavigation(state, action = {}) {
  const current = state || createDiscussionBranchNavigationState();

  switch (action.type) {
    case DISCUSSION_NAVIGATION_ACTIONS.RESET:
      return createDiscussionBranchNavigationState(action.request);
    case DISCUSSION_NAVIGATION_ACTIONS.OPEN_CHILDREN:
      return {
        activePath: selectBranchPath(current.activePath, action.reply, action.threadId),
        composer: null,
        scrollTarget: firstChildScrollTarget(action.reply?.id),
      };
    case DISCUSSION_NAVIGATION_ACTIONS.OPEN_REPLY_COMPOSER:
      return {
        activePath: selectBranchPath(current.activePath, action.reply, action.threadId),
        composer: DISCUSSION_COMPOSER_TARGETS.REPLY,
        scrollTarget: null,
      };
    case DISCUSSION_NAVIGATION_ACTIONS.OPEN_ROOT_COMPOSER:
      return { ...current, composer: DISCUSSION_COMPOSER_TARGETS.ROOT, scrollTarget: null };
    case DISCUSSION_NAVIGATION_ACTIONS.CLOSE_COMPOSER:
      return { ...current, composer: null };
    case DISCUSSION_NAVIGATION_ACTIONS.CONSUME_SCROLL_TARGET:
      return { ...current, scrollTarget: null };
    case DISCUSSION_NAVIGATION_ACTIONS.BACK:
      return {
        activePath: current.activePath.slice(0, -1),
        composer: null,
        scrollTarget: null,
      };
    default:
      return current;
  }
}
